import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withMsal } from '@azure/msal-react';
import { connect } from 'react-redux';
import { HttpMethod, HttpRequest } from '../shared/core/utils/requestHandler.js';
import { con } from '../shared/config/config';
import { history } from '../shared/core/history';
import { LOGIN_SUCCESS, LOGIN_UNAUTHORIZED, SESSION_EXPIRED, SESSION_RESET, LOGIN_REQUESTING } from '../redux/constants/shared/loginUserConstants';

class SsoAuthHandler extends Component {
    constructor(props) {
        super(props);
        this.state = {
            authProcessed: false,
            isProcessing: false
        };
    }

    componentDidMount() {
        // Use live MSAL cache (getAllAccounts) instead of React props which may
        // be stale — e.g. after logout the props haven't re-rendered yet but
        // the cache has already been cleared by optionHandle / clearMsalSession.
        const msalInstance = this.props.msalContext?.instance;
        const liveAccounts = msalInstance?.getAllAccounts?.() || [];

        if (liveAccounts.length > 0) {
            this.checkAuthentication();
        }
    }

    componentDidUpdate(prevProps) {
        // Case 1: MSAL just became authenticated (fresh loginRedirect completed)
        if (!prevProps.isAuthenticated && this.props.isAuthenticated && this.props.accounts.length > 0) {
            if (this.props.isSessionExpired || this.props.isUnauthorized) {
                this.props.dispatch({ type: SESSION_RESET });
                return;
            }
            this.checkAuthentication();
        }

        // Case 2: Redux login.successful just flipped from true → false while MSAL
        // still has accounts (ProtectedRoute redirected to /login after token expiry).
        // Skip if a logout is in progress — the user intentionally logged out.
        if (prevProps.loginSuccess && !this.props.loginSuccess && this.props.isAuthenticated && !this.props.isLoggingOut) {
            this.setState({ authProcessed: false, isProcessing: false }, () => {
                this.checkAuthentication();
            });
        }
    }

    /**
     * Parse JWT token to extract user identifiers
     * @param {string} idToken - JWT token
     * @param {object} account - MSAL account object
     * @returns {object} - { userSub, fidId, payload }
     */
    parseTokenPayload(idToken, account) {
        try {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const userSub = payload.sub || payload.oid || 
                (account.homeAccountId ? account.homeAccountId.split('.')[0] : null);
            const fidId = payload.fid_id || null;
            return { userSub, fidId, payload };
        } catch (e) {
            console.warn('SsoAuthHandler: failed to parse id_token payload', e);
            const userSub = account.homeAccountId ? account.homeAccountId.split('.')[0] : null;
            return { userSub, fidId: null, payload: null };
        }
    }

    /**
     * Calculate token expiration in seconds
     * @param {Date|number} expiresOn - Token expiration timestamp
     * @returns {number} - Seconds until expiration
     */
    calculateExpiresIn(expiresOn) {
        if (!expiresOn) {
            return 3599; // Default to 1 hour
        }
        const expiresOnTime = expiresOn instanceof Date 
            ? expiresOn.getTime() 
            : expiresOn;
        return Math.floor((expiresOnTime - Date.now()) / 1000);
    }

    /**
     * Call loginAdmin API to verify user authorization
     * @param {string} idToken - JWT token
     * @param {string} fidId - User FID
     * @returns {Promise<object>} - API response
     */
    async callLoginAdminApi(idToken, fidId) {
        const endpoint = `${con.adminUrl}/products/${con.PRODUCT_CODE}/users/${fidId}/login`;

        return new HttpRequest(HttpMethod.POST, endpoint, null, {}, {
            headers: {
                'authorization': `Bearer ${idToken}`,
                'fid-ent-identity-jwt': `Bearer ${idToken}`,
            },
            credentials: 'include'
        }).invoke();
    }

    /**
     * Establish backend session in Hazelcast
     * @param {object} tokenResponse - MSAL token response
     * @param {object} payload - JWT payload
     * @param {string} userSub - User subject ID
     * @param {object} userProfile - User profile from loginAdmin
     */
    async establishBackendSession(tokenResponse, payload, userSub, userProfile) {
        try {
            const expiresInSeconds = this.calculateExpiresIn(tokenResponse.expiresOn);

            await new HttpRequest(
                HttpMethod.POST,
                '/api/establish-session',
                null,
                {
                    user: {
                        name: payload.name,
                        email: payload.preferred_username || payload.email,
                        sub: userSub
                    },
                    exp: payload.exp,
                    user_profile: userProfile,
                    idToken: tokenResponse.idToken,
                    accessToken: tokenResponse.accessToken,
                    expiresIn: expiresInSeconds
                },
                {
                    credentials: 'include'
                }
            ).invoke();

            console.log('Backend session established in Hazelcast (Azure AD SSO format)');
        } catch (sessionErr) {
            console.error('Failed to establish backend session:', sessionErr);
        }
    }

    /**
     * Handle successful authentication
     * @param {object} tokenResponse - MSAL token response
     * @param {object} loginAdminResponse - loginAdmin API response
     * @param {string} userSub - Azure AD subject ID (opaque hash)
     * @param {string} fidId - Fidelity user ID (e.g. a752288) used by admin API
     */
    async handleAuthenticationSuccess(tokenResponse, loginAdminResponse, userSub, fidId) {
        const { dispatch } = this.props;
        const payload = JSON.parse(atob(tokenResponse.idToken.split('.')[1]));

        await this.establishBackendSession(tokenResponse, payload, fidId, loginAdminResponse.data);

        console.log('SsoAuthHandler: Session established, dispatching LOGIN_REQUESTING to fetch enriched user profile');
        dispatch({ type: LOGIN_REQUESTING });

        this.setState({ isProcessing: false });
    }

    /**
     * Handle 401 unauthorized error
     * @param {object} msalContext - MSAL context
     * @param {object} account - User account
     */
    async handleUnauthorizedError(msalContext, account) {
        console.warn('User is not authorized to access this application');
        const { dispatch } = this.props;

        this.setState({ isProcessing: false, authProcessed: true });
        dispatch({ type: LOGIN_UNAUTHORIZED });
        // Navigate to /error so the unauthorized message is shown instead of a
        // blank page.  The routes are now rendered when unauthorized === true.
        history.push('/error');
    }

    /**
     * Handle API errors
     * @param {Error} apiErr - API error object
     * @param {object} msalContext - MSAL context
     * @param {object} account - User account
     */
    async handleApiError(apiErr, msalContext, account) {
        const { dispatch } = this.props;
        
        console.error('SsoAuthHandler: loginAdmin API failed', apiErr);

        if (apiErr.response && (apiErr.response.status === 401 || apiErr.response.status === 403)) {
            await this.handleUnauthorizedError(msalContext, account);
            return;
        }

        // For other errors, reset state and allow retry
        this.setState({ isProcessing: false, authProcessed: false });
        dispatch({ type: 'LOGIN_REQUESTING' });
    }

    /**
     * Process authentication after acquiring tokens
     * @param {object} tokenResponse - MSAL token response
     */
    async processAuthentication(tokenResponse) {
        const { msalContext, accounts } = this.props;

        if (!tokenResponse || !tokenResponse.idToken) {
            return;
        }

        const { userSub, fidId } = this.parseTokenPayload(tokenResponse.idToken, accounts[0]);

        if (!userSub) {
            console.error('[SsoAuthHandler] processAuthentication: cannot determine userSub — aborting');
            this.setState({ isProcessing: false });
            return;
        }

        if (!fidId) {
            console.error('[SsoAuthHandler] processAuthentication: cannot determine fidId — aborting');
            this.setState({ isProcessing: false });
            return;
        }

        try {
            const payload = JSON.parse(atob(tokenResponse.idToken.split('.')[1]));

            // Step 1: Establish a preliminary backend session (no user_profile yet)
            // so that auth.check passes for the loginAdmin proxy call.
            await this.establishBackendSession(tokenResponse, payload, fidId, null);

            // Step 2: Call loginAdmin — now authorized because session exists
            const loginAdminResponse = await this.callLoginAdminApi(tokenResponse.idToken, fidId);

            if (loginAdminResponse && loginAdminResponse.status === 200) {
                // Step 3: Update session with the real user_profile from loginAdmin
                await this.handleAuthenticationSuccess(tokenResponse, loginAdminResponse, userSub, fidId);
            }
        } catch (apiErr) {
            await this.handleApiError(apiErr, msalContext, accounts[0]);
        }
    }

    checkAuthentication = async () => {
        const { msalContext, isAuthenticated, accounts, dispatch, isSessionExpired, isUnauthorized, isLoggingOut } = this.props;

        if (isSessionExpired || isUnauthorized || isLoggingOut) {
            return;
        }

        if (this.state.authProcessed || this.state.isProcessing || !isAuthenticated || accounts.length === 0) {
            return;
        }

        this.setState({ authProcessed: true, isProcessing: true });

        try {
            const request = {
                scopes: ["openid", "profile", "User.Read"],
                account: accounts[0]
            };

            const tokenResponse = await msalContext.instance.acquireTokenSilent(request);
            await this.processAuthentication(tokenResponse);
        } catch (error) {
            if (error.errorCode === 'consent_required' ||
                error.errorCode === 'interaction_required' ||
                error.errorCode === 'login_required' ||
                error.name === 'InteractionRequiredAuthError') {

                console.warn('[SsoAuthHandler] Token expired — dispatching SESSION_EXPIRED');
                this.setState({ isProcessing: false, authProcessed: true });
                dispatch({ type: SESSION_EXPIRED });
            } else {
                this.setState({ authProcessed: false, isProcessing: false });
            }
        }
    };

    render() {
        return null;
    }
}

// PropTypes validation
SsoAuthHandler.propTypes = {
    msalContext: PropTypes.shape({
        instance: PropTypes.shape({
            acquireTokenSilent: PropTypes.func.isRequired,
            logoutRedirect: PropTypes.func,
            getAllAccounts: PropTypes.func,
            getActiveAccount: PropTypes.func,
            initialize: PropTypes.func
        }).isRequired,
        accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
        inProgress: PropTypes.string,
        logger: PropTypes.object
    }).isRequired,
    accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    loginRequesting: PropTypes.bool,
    isSessionExpired: PropTypes.bool,
    isUnauthorized: PropTypes.bool,
    loginSuccess: PropTypes.bool,
    isLoggingOut: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    loginRequesting: state.login ? state.login.requesting : false,
    isSessionExpired: state.login ? state.login.sessionExpired : false,
    isUnauthorized: state.login ? state.login.unauthorized : false,
    loginSuccess: state.login ? state.login.successful : false,
    isLoggingOut: state.login ? state.login.loggingOut : false,
});

const mapDispatchToProps = (dispatch) => ({
    dispatch
});

const withMsalAndAuth = (WrappedComponent) => {
    return withMsal((props) => {
        const { msalContext } = props;
        const accounts = msalContext.accounts;
        const isAuthenticated = accounts.length > 0;
        
        return (
            <WrappedComponent 
                {...props} 
                accounts={accounts}
                isAuthenticated={isAuthenticated}
            />
        );
    });
};

// Export unwrapped component for testing
export { SsoAuthHandler as UnwrappedSsoAuthHandler };

export default connect(mapStateToProps, mapDispatchToProps)(withMsalAndAuth(SsoAuthHandler));